import { useCallback, useEffect, useRef, useState } from "react";

type UsePopoverResult = {
  open: boolean;
  pinned: boolean;
  anchorRect: DOMRect | null;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  show: () => void;
  scheduleHide: () => void;
  cancelHide: () => void;
  togglePin: () => void;
  close: () => void;
};

// Hover/pin popover state. show() opens (e.g. on hover), togglePin() pins on
// click, scheduleHide() begins a timer to hide on hover-out (no-op when pinned).
export function usePopover(): UsePopoverResult {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const show = useCallback(() => {
    cancelHide();
    if (anchorRef.current) {
      setAnchorRect(anchorRef.current.getBoundingClientRect());
    }
    setOpen(true);
  }, [cancelHide]);

  const scheduleHide = useCallback(() => {
    cancelHide();
    if (pinned) return;
    timer.current = setTimeout(() => setOpen(false), 140);
  }, [cancelHide, pinned]);

  const close = useCallback(() => {
    cancelHide();
    setOpen(false);
    setPinned(false);
  }, [cancelHide]);

  const togglePin = useCallback(() => {
    if (open && pinned) {
      close();
    } else {
      if (anchorRef.current) {
        setAnchorRect(anchorRef.current.getBoundingClientRect());
      }
      setOpen(true);
      setPinned(true);
    }
  }, [open, pinned, close]);

  useEffect(() => () => cancelHide(), [cancelHide]);

  return {
    open,
    pinned,
    anchorRect,
    anchorRef,
    show,
    scheduleHide,
    cancelHide,
    togglePin,
    close,
  };
}
